import {
  parseUnits,
  keccak256,
  encodePacked,
  hexToBytes,
  encodeFunctionData
} from "viem";
import { signMessage, readContract, getAccount } from "@wagmi/core";
import { signSellOrderApi, writeContractApi } from "../api/methods-marketplace";
import { addToast } from "@heroui/react";
import nftABI from "../abis/nft-abi.json";
import ApproveContract from "./approveContract";
import { rainbowKitConfig } from "../utils/config";

const signSellOrder = async ({
  slug,
  item,
  erc20BAFAddress,
  nftAddress,
  transferProxyAddress,
  isCustodialWallet = false,
  address = null,
  publicClient = null
}) => {
  console.log("signSellOrder called with:", isCustodialWallet, address, publicClient);
  const missingParams = [];
  if (!slug) missingParams.push("slug");
  if (!item) missingParams.push("item");
  if (!erc20BAFAddress) missingParams.push("erc20BAFAddress");
  if (!nftAddress) missingParams.push("nftAddress");
  if (!transferProxyAddress) missingParams.push("transferProxyAddress");

  if (missingParams.length > 0) {
    throw new Error(`Missing required parameters: ${missingParams.join(", ")}`);
  }

  const collection = item?.data?.collection;
  const resale_price = collection?.resale_price ?? null;
  const sale_price = resale_price || collection?.instant_sale_price || null;
  const tokenId = collection?.token;
  const parsedTokenId = BigInt(tokenId);
  const physical_asset = collection?.physical_nft;
  const decimals = 18;

  if (!sale_price || (parseFloat(sale_price) <= 0 && physical_asset)) {
    addToast({
      title: "Invalid Price",
      description: "Sale price must be greater than 0",
      color: "danger",
      variant: "bordered"
    });
    return;
  }

  try {
    if (!isCustodialWallet && !address) {
      addToast({
        title: "Wallet Not Connected",
        description: "Please connect your wallet to proceed.",
        color: "danger",
        variant: "bordered"
      });
      return;
    }

    let isApproved = false;

    if (isCustodialWallet) {
      if (!publicClient) {
        throw new Error("publicClient is required for custodial wallet");
      }
      const approvedAddress = await publicClient.readContract({
        address: nftAddress,
        abi: nftABI,
        functionName: "getApproved",
        args: [parsedTokenId]
      });

      isApproved =
        approvedAddress.toLowerCase() === transferProxyAddress.toLowerCase();
    } else {
      const approvedAddress = await readContract(rainbowKitConfig, {
        address: nftAddress,
        abi: nftABI,
        functionName: "getApproved",
        args: [tokenId]
      });
      isApproved =
        approvedAddress.toLowerCase() === transferProxyAddress.toLowerCase();
    }

    if (!isApproved) {
      await ApproveContract(
        nftAddress,
        transferProxyAddress,
        tokenId,
        isCustodialWallet,
        publicClient
      );
    }

    const nonce_value = Math.floor(Date.now() / 1000);
    const unitPrice = parseUnits(sale_price.toString(), decimals);

    const wethMessageHash = keccak256(
      encodePacked(
        ["address", "uint256", "address", "uint256", "uint256"],
        [
          nftAddress,
          parsedTokenId,
          erc20BAFAddress,
          unitPrice,
          BigInt(nonce_value)
        ]
      )
    );

    if (isCustodialWallet) {
      const signResult = await signSellOrderApi({
        slug,
        instant_sale_price: sale_price,
        nonce: nonce_value,
        message_hash: wethMessageHash,
        is_custodial: true
      });

      if (signResult?.status !== 200) {
        throw new Error(
          signResult?.data?.message || "Custodial listing failed"
        );
      }

      addToast({
        title: "Listing Created Successfully",
        description: "Your NFT has been listed for sale!",
        color: "success",
        variant: "bordered"
      });
      setTimeout(() => window.location.reload(), 800);
      return;
    }

    const messageHashBytes = hexToBytes(wethMessageHash);
    const fixedPriceSignaturePMT = await signMessage(rainbowKitConfig, {
      message: { raw: messageHashBytes }
    });

    const result = await signSellOrderApi({
      slug,
      instant_sale_price: sale_price,
      sign_instant_sale_price: fixedPriceSignaturePMT,
      nonce: nonce_value
    });

    if (result?.status === 200) {
      addToast({
        title: "Listing Created Successfully",
        description: "Your NFT has been listed for sale!",
        color: "success",
        variant: "bordered"
      });
      setTimeout(() => window.location.reload(), 800);
    } else {
      addToast({
        title: "Listing Failed",
        description: "Failed to create NFT listing.",
        color: "danger",
        variant: "bordered"
      });
    }
  } catch (err) {
    const userRejected =
      err?.code === 4001 || err?.message?.includes("User rejected");

    if (userRejected) {
      addToast({
        title: "Transaction Rejected",
        description: "You rejected the transaction from your wallet.",
        color: "warning",
        variant: "bordered"
      });
    } else {
      addToast({
        title: "Sign Order Failed",
        description: err?.message || "Something went wrong. Please try again.",
        color: "danger",
        variant: "bordered"
      });
    }
  }
};

export default signSellOrder;

